/**
 * Catálogo de permisos y roles (RBAC) y ámbitos (ABAC).
 *
 * Este catálogo es la fuente de verdad: las filas de la tabla `permission`
 * se siembran desde aquí. Un permiso que no esté en este fichero no existe.
 *
 * Modelo (SECURITY.md §3):
 *   RBAC → qué acciones puede hacer un rol
 *   ABAC → sobre qué subconjunto de datos (delegación, cliente, centro, almacén)
 *   RLS  → red de seguridad en la base de datos, independiente de lo anterior
 */

export const MODULES = [
  'platform',
  'identity',
  'customers',
  'assets',
  'regulation',
  'maintenance',
  'fieldservice',
  'workshop',
  'inventory',
  'purchasing',
  'sales',
  'billing',
  'treasury',
  'crm',
  'documents',
  'audit',
  'sync',
  'reporting',
] as const;

export type Module = (typeof MODULES)[number];

/** `modulo.accion` — ver API-DESIGN.md §2. */
export type Permission = string;

function perms(module: Module, actions: readonly string[]): Permission[] {
  return actions.map((a) => `${module}.${a}`);
}

const CRUD = ['read', 'create', 'update', 'delete'] as const;

export const PERMISSIONS: readonly Permission[] = Object.freeze([
  ...perms('platform', ['read', 'update', 'manage_companies', 'manage_branches']),
  ...perms('identity', [...CRUD, 'manage_roles', 'manage_devices', 'revoke_device', 'impersonate']),
  ...perms('customers', [...CRUD, 'export']),
  ...perms('assets', [...CRUD, 'bulk_import', 'print_labels', 'move', 'withdraw']),
  ...perms('regulation', ['read', 'create', 'update', 'publish', 'impact_report']),
  ...perms('maintenance', [...CRUD, 'generate_plan', 'schedule']),
  ...perms('fieldservice', [...CRUD, 'execute', 'close', 'sign', 'reopen']),
  ...perms('workshop', [...CRUD, 'receive', 'repair', 'dispatch']),
  ...perms('inventory', [...CRUD, 'adjust', 'transfer', 'reserve', 'read_cost']),
  ...perms('purchasing', [...CRUD, 'approve', 'receive']),
  ...perms('sales', [...CRUD, 'send', 'accept', 'read_margin']),
  ...perms('billing', [...CRUD, 'emit', 'rectify', 'bulk_invoice', 'read_registry']),
  ...perms('treasury', [...CRUD, 'remittance', 'reconcile', 'read_bank_details']),
  ...perms('crm', [...CRUD, 'assign']),
  ...perms('documents', ['read', 'create', 'download', 'sign']),
  ...perms('audit', ['read', 'export']),
  ...perms('sync', ['push', 'pull', 'resolve_conflict', 'read_fleet']),
  ...perms('reporting', ['read', 'export', 'read_financial']),
]);

const PERMISSION_SET = new Set(PERMISSIONS);

export function isKnownPermission(value: string): value is Permission {
  return PERMISSION_SET.has(value);
}

/** Roles iniciales (§81 del prompt maestro). */
export const ROLES = [
  'SUPERADMIN',
  'DIRECCION',
  'ADMINISTRACION',
  'RESPONSABLE_TECNICO',
  'TECNICO',
  'COMERCIAL',
  'ALMACEN',
  'COMPRAS',
  'CONTABILIDAD',
  'TESORERIA',
  'LECTURA',
  'CLIENTE',
] as const;

export type Role = (typeof ROLES)[number];

/** Comodín: `assets.*` concede todas las acciones del módulo. */
export const ALL = '*';

const P = (module: Module) => `${module}.${ALL}`;

export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = Object.freeze({
  SUPERADMIN: [ALL],

  DIRECCION: [
    P('platform'),
    'identity.read',
    P('customers'),
    P('assets'),
    'regulation.read',
    P('maintenance'),
    P('fieldservice'),
    P('workshop'),
    P('inventory'),
    P('purchasing'),
    P('sales'),
    P('billing'),
    P('treasury'),
    P('crm'),
    P('documents'),
    P('audit'),
    'sync.read_fleet',
    P('reporting'),
  ],

  ADMINISTRACION: [
    'identity.read',
    // Perder un móvil es un incidente operativo cotidiano: exigir un
    // superadministrador para revocarlo garantizaría que no se revoque
    // (SECURITY.md §6).
    'identity.manage_devices',
    'identity.revoke_device',
    P('customers'),
    'assets.read',
    'assets.update',
    'assets.print_labels',
    'regulation.read',
    P('maintenance'),
    P('fieldservice'),
    'workshop.read',
    'inventory.read',
    'inventory.reserve',
    P('sales'),
    P('billing'),
    'treasury.read',
    'treasury.create',
    'treasury.update',
    'treasury.remittance',
    'crm.read',
    P('documents'),
    'audit.read',
    'sync.resolve_conflict',
    'sync.read_fleet',
    'reporting.read',
    'reporting.export',
  ],

  RESPONSABLE_TECNICO: [
    'identity.read',
    // Gestiona la flota de terminales de campo.
    'identity.manage_devices',
    'identity.revoke_device',
    // Da de alta a sus propios técnicos. Quien incorpora a la gente al
    // equipo es quien la conoce, y hacer pasar cada alta por un
    // superadministrador garantiza que se acabe compartiendo una cuenta.
    //
    // No es una vía de escalada: `UsersService.create` impide conceder
    // permisos que uno mismo no tiene, así que desde aquí no se puede
    // fabricar un superadministrador.
    'identity.create',
    'identity.update',
    'identity.manage_roles',
    // Necesarios para poder dar de alta técnicos: la regla «no se puede
    // conceder lo que uno mismo no tiene» exige tenerlos para otorgar el rol
    // TECNICO. Y encajan con el puesto: quien supervisa campo lleva la app.
    'sync.push',
    'sync.pull',
    'customers.read',
    'customers.update',
    P('assets'),
    'regulation.read',
    'regulation.impact_report',
    P('maintenance'),
    P('fieldservice'),
    P('workshop'),
    'inventory.read',
    'inventory.reserve',
    'inventory.transfer',
    'purchasing.read',
    'purchasing.create',
    'sales.read',
    'sales.create',
    'documents.read',
    'documents.create',
    'documents.download',
    'audit.read',
    'sync.read_fleet',
    'sync.resolve_conflict',
    'reporting.read',
  ],

  // El técnico NO tiene inventory.read_cost ni sales.read_margin (SECURITY.md §3)
  TECNICO: [
    'customers.read',
    'assets.read',
    'assets.create',
    'assets.update',
    'assets.move',
    'assets.withdraw',
    'assets.print_labels',
    'regulation.read',
    'maintenance.read',
    'fieldservice.read',
    'fieldservice.execute',
    'fieldservice.close',
    'fieldservice.sign',
    'workshop.read',
    'workshop.create',
    'inventory.read',
    'documents.read',
    'documents.create',
    'sync.push',
    'sync.pull',
  ],

  COMERCIAL: [
    'customers.read',
    'customers.create',
    'customers.update',
    'assets.read',
    'maintenance.read',
    'sales.read',
    'sales.create',
    'sales.update',
    'sales.send',
    'sales.accept',
    P('crm'),
    'documents.read',
    'documents.create',
    'documents.download',
    'sync.push',
    'sync.pull',
    'reporting.read',
  ],

  ALMACEN: [
    'customers.read',
    'assets.read',
    P('inventory'),
    'purchasing.read',
    'purchasing.receive',
    'fieldservice.read',
    'workshop.read',
    'documents.read',
    'reporting.read',
  ],

  COMPRAS: [
    'inventory.read',
    'inventory.read_cost',
    P('purchasing'),
    'documents.read',
    'documents.create',
    'reporting.read',
  ],

  CONTABILIDAD: [
    'customers.read',
    'sales.read',
    P('billing'),
    'treasury.read',
    'purchasing.read',
    'documents.read',
    'documents.download',
    'audit.read',
    'reporting.read',
    'reporting.export',
    'reporting.read_financial',
  ],

  TESORERIA: [
    'customers.read',
    'billing.read',
    'billing.read_registry',
    P('treasury'),
    'documents.read',
    'reporting.read',
    'reporting.read_financial',
  ],

  LECTURA: [
    'customers.read',
    'assets.read',
    'regulation.read',
    'maintenance.read',
    'fieldservice.read',
    'workshop.read',
    'inventory.read',
    'sales.read',
    'documents.read',
    'reporting.read',
  ],

  // El portal del cliente: sólo lectura y sólo de SU customer_id (ABAC)
  CLIENTE: ['customers.read', 'assets.read', 'fieldservice.read', 'documents.read', 'documents.download'],
});

/**
 * ¿El conjunto de permisos concedidos satisface el requerido?
 * Soporta el comodín global `*` y por módulo `modulo.*`.
 */
export function hasPermission(granted: Iterable<string>, required: Permission): boolean {
  const set = granted instanceof Set ? granted : new Set(granted);
  if (set.has(ALL)) return true;
  if (set.has(required)) return true;
  const dot = required.indexOf('.');
  if (dot > 0) return set.has(`${required.slice(0, dot)}.${ALL}`);
  return false;
}

export function hasAllPermissions(granted: Iterable<string>, required: readonly Permission[]): boolean {
  const set = granted instanceof Set ? granted : new Set(granted);
  return required.every((r) => hasPermission(set, r));
}

/** Expande los roles de un usuario a su conjunto efectivo de permisos. */
/**
 * Qué le falta a alguien para poder conceder estos roles.
 *
 * La regla es «no se puede conceder lo que uno mismo no tiene». Sin ella,
 * el permiso de gestionar roles es una escalada de privilegios en un paso:
 * quien puede crear usuarios se crea uno superadministrador y entra con él.
 *
 * Se compara sobre los PERMISOS que otorga cada rol, no sobre sus nombres.
 * Así la regla sigue valiendo el día que alguien cambie qué concede un rol,
 * que es justo el día en que una lista de nombres prohibidos se quedaría
 * obsoleta sin que nadie se diera cuenta.
 *
 * Devuelve la lista vacía cuando sí puede concederlos.
 */
export function missingToGrant(granted: Iterable<string>, roles: readonly Role[]): string[] {
  const set = granted instanceof Set ? granted : new Set(granted);
  return [...permissionsForRoles(roles)]
    .filter((permission) => !hasPermission(set, permission as Permission))
    .sort();
}

export function permissionsForRoles(roles: readonly Role[]): Set<string> {
  const out = new Set<string>();
  for (const role of roles) {
    for (const p of ROLE_PERMISSIONS[role] ?? []) out.add(p);
  }
  return out;
}
